/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createVisitSchema, type VisitFormData } from './schema';
import { toIsoFormat } from '@pages/authenticated/agency-pages/visits/visits-form-initial-values';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import type { VisitInput } from '@services/requests/housien-models/VisitInput';
import { parseBrtDateTime } from '@pages/authenticated/agency-pages/visits/visit-time-conflict';
import { useSonnerToast } from '@/shared/ui';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';

export interface UseVisitDrawerFormProps {
  agencyId: string;
  userEmail: string;
  /**
   * Whether the drawer is showing.
   *
   * The drawer stays mounted and is only hidden, so without this the
   * form keeps whatever the last opening left in it -- and, worse, holds
   * the prefill it was built with on the very first mount, before the
   * attendance it was opened from had loaded.
   */
  open?: boolean;
  editingVisit?: any;
  /** Prefill client when creating from a client profile. */
  initialClientId?: string;
  /**
   * Prefill the attendance when creating from a deal's detail screen.
   * A set `dealId` routes the create through
   * `POST /deals/{id}/visits`, so the visit lands under that attendance
   * instead of opening a new one.
   */
  initialDealId?: string;
  /** Prefill the property when creating from a deal that already has one. */
  initialPropertyId?: string;
  onSuccess?: () => void;
}

/**
 * Splits a stored BRT datetime string into separate date and time parts.
 *
 * @param dt - The visit datetime string, or undefined.
 * @returns The date and time parts, both empty when no datetime is given.
 */
function parseVisitDateTime(dt: string | undefined): { date: string; time: string } {
  if (!dt) return { date: '', time: '' };
  return parseBrtDateTime(dt);
}

/** The ids a caller can prefill on a brand-new visit. */
interface VisitPrefill {
  clientId?: string;
  dealId?: string;
  propertyId?: string;
}

/**
 * Builds blank form values for a new visit, optionally prefilling the
 * client, attendance and property a caller already knows.
 *
 * @param userEmail - The current user's email, used as the default broker.
 * @param prefill - Ids to seed the form with.
 * @returns The empty visit form values.
 */
function emptyFormValues(userEmail: string, prefill: VisitPrefill = {}): VisitFormData {
  return {
    propertyId: prefill.propertyId ?? '',
    clientId: prefill.clientId ?? '',
    dealId: prefill.dealId ?? '',
    visitDate: '',
    visitTime: '',
    brokerEmail: userEmail,
    notes: '',
  };
}

/**
 * Maps an existing visit record into editable form values.
 *
 * @param visit - The visit being edited.
 * @param userEmail - Fallback broker email when the visit has none.
 * @returns The prefilled visit form values.
 */
function editFormValues(visit: any, userEmail: string): VisitFormData {
  const { date: visitDate, time: visitTime } = parseVisitDateTime(visit.visit_datetime);
  return {
    propertyId: visit.property_ids?.[0] ?? '',
    clientId: visit.client_id ?? '',
    dealId: visit.deal_id ?? '',
    visitDate,
    visitTime,
    brokerEmail: visit.broker_emails?.[0] ?? userEmail,
    notes: visit.notes ?? '',
  };
}

/**
 * Chooses empty or edit form values depending on whether a visit is being edited.
 *
 * @param props - The drawer form props (editing visit, user email, client id).
 * @returns The initial form values.
 */
function buildDefaultValues(props: UseVisitDrawerFormProps): VisitFormData {
  if (props.editingVisit) return editFormValues(props.editingVisit, props.userEmail);
  return emptyFormValues(props.userEmail, {
    clientId: props.initialClientId,
    dealId: props.initialDealId,
    propertyId: props.initialPropertyId,
  });
}

/**
 * Persists the visit via the right endpoint (update, schedule-under-deal, or
 * plain schedule) and returns the i18n key for the success toast to show.
 *
 * @param agencyId - The agency the visit belongs to.
 * @param editingVisit - The visit being edited, if any.
 * @param dealId - The deal to schedule under, if creating from one.
 * @param editablePayload - The PUT-safe payload for updates.
 * @param createPayload - The full payload for creates.
 * @returns The i18n key for the success toast.
 */
async function persistVisit(
  agencyId: string,
  editingVisit: UseVisitDrawerFormProps['editingVisit'],
  dealId: string | undefined,
  editablePayload: VisitInput,
  createPayload: VisitInput,
): Promise<string> {
  if (editingVisit) {
    await VisitsService.updateVisit(agencyId, editingVisit.visit_id, editablePayload);
    return 'toast.visitUpdated';
  }
  if (dealId) {
    await VisitsService.scheduleVisitUnderDeal(agencyId, dealId, createPayload);
    return 'toast.visitScheduled';
  }
  await VisitsService.scheduleVisit(agencyId, createPayload);
  return 'toast.visitScheduled';
}

/**
 * Form controller for the visit drawer: wires the zod schema, default values,
 * submit/validation state, and the create-or-update side effect.
 *
 * @param props - The agency and user context, optional visit to edit, prefilled
 *   client, and a success callback.
 * @returns The form instance plus submit handler and submitting/edit flags.
 */
export function useVisitDrawerForm(props: UseVisitDrawerFormProps) {
  const { agencyId, editingVisit, onSuccess } = props;
  const { t } = useTranslation('features/visits');
  const { showSuccess } = useSonnerToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const defaultValues = useMemo(() => buildDefaultValues(props), [
    editingVisit?.visit_id, props.userEmail, agencyId,
    props.initialClientId, props.initialDealId, props.initialPropertyId,
  ]);

  const schema = useMemo(() => createVisitSchema(t), [t]);

  const form = useForm<VisitFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });

  // `useForm` reads `defaultValues` once, at mount. The drawer mounts
  // with the screen -- before the attendance has loaded -- so on the
  // detail screen `initialDealId` was still undefined then, became '' by
  // the schema's default, and '' is falsy: every visit booked from an
  // attendance quietly took the generic endpoint and came back with no
  // `deal_id`. Which is the link the Visitando column and the stage
  // trigger both read.
  //
  // Only on the shut-to-open edge. Resetting whenever `defaultValues`
  // changes identity wipes the date the broker just picked, and the
  // submit then fails validation with the drawer still full.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) form.reset(defaultValues);
    wasOpen.current = Boolean(props.open);
  }, [props.open, defaultValues, form]);

  const onValid = useCallback(async (data: VisitFormData) => {
    setIsSubmitting(true);
    setHasValidationErrors(false);
    // Only the fields the API accepts on PUT. owner_email is immutable
    // server-side and status is owned by the confirm/cancel/done actions,
    // so sending either here makes the update 400 or silently downgrade
    // a confirmed visit back to pending.
    const editablePayload: VisitInput = {
      property_ids: [data.propertyId],
      visit_datetime: toIsoFormat(data.visitDate, data.visitTime),
      broker_emails: [data.brokerEmail],
      client_id: data.clientId,
      notes: data.notes,
    };
    const createPayload: VisitInput = {
      ...editablePayload,
      // Required by the API and never sent until now, so every create
      // from this drawer came back 400. It is the responsible broker:
      // the detail views already read it that way.
      owner_email: data.brokerEmail,
      status: 'pending',
    };

    try {
      const successKey = await persistVisit(agencyId, editingVisit, data.dealId, editablePayload, createPayload);
      showSuccess(t(successKey));
      onSuccess?.();
    } catch (error) {
      reportError(error, {
        message: editingVisit
          ? t('toast.updateFailed')
          : t('toast.scheduleFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [agencyId, editingVisit, onSuccess, showSuccess, t]);

  const onInvalid = useCallback(() => {
    setHasValidationErrors(true);
  }, []);

  const handleSubmit = useCallback(
    () => form.handleSubmit(onValid, onInvalid)(),
    [form, onValid, onInvalid]
  );

  return {
    form,
    isSubmitting,
    hasValidationErrors,
    handleSubmit,
    isEditMode: !!editingVisit,
  };
}
